/**
 * Ad measurement.
 *
 * Every ad slot reports three things to GA4 / GTM:
 *  - `ad_request`    — a slot was mounted and asked the network for an ad
 *  - `ad_impression` — the slot actually filled (a creative is on screen)
 *  - `ad_unfilled`   — the network returned nothing
 *  - `ad_click`      — the visitor engaged with the creative (best-effort:
 *                      a cross-origin creative cannot be inspected, so we
 *                      infer the click from focus leaving the page while the
 *                      pointer is over the slot)
 *
 * Requests vs impressions give the real fill rate, which is what we need to
 * price paid picks. Slot/page/sport/device dimensions are attached to each
 * event so placements can be compared against each other.
 *
 * A click also stores a short-lived attribution record, so a sign-up that
 * follows an ad click can be reported as an ad-to-signup conversion.
 */

import { trackEvent } from "@/lib/analytics";

/**
 * Mirror the event into our own database so ad performance can be read on the
 * admin screen (GA4 alone is not queryable from the app). Fire-and-forget: ad
 * telemetry must never block or break the page.
 */
function logAdEvent(
  eventType: "request" | "impression" | "unfilled" | "click" | "signup",
  props: Record<string, unknown>,
) {
  if (typeof window === "undefined") return;
  try {
    void fetch("/api/public/ad-event", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        event_type: eventType,
        ad_network: String(props['ad_network'] ?? "adsense"),
        ad_unit: props['ad_unit'] ? String(props['ad_unit']) : undefined,
        placement: props['ad_placement'] ? String(props['ad_placement']) : undefined,
        ad_size: props['ad_size'] ? String(props['ad_size']) : undefined,
        page_path: props['page_path'] ? String(props['page_path']) : undefined,
        sport: props['sport'] ? String(props['sport']) : undefined,
        device_class: props['device_class'] ? String(props['device_class']) : undefined,
      }),
    }).catch(() => undefined);
  } catch {
    /* ignore */
  }
}

/** Record an ad-attributed sign-up in our own ad reporting. */
export function logAdSignupConversion(props: Record<string, unknown>) {
  logAdEvent("signup", props);
}

const FILL_STORAGE_KEY = "statlign_ad_fill_v1";
const ATTRIBUTION_KEY = "statlign_ad_referral_v1";
/** A sign-up within this window of an ad click counts as ad-attributed. */
const ATTRIBUTION_WINDOW_MS = 6 * 60 * 60 * 1000;

export interface AdSlotContext {
  /** Ad unit key. */
  unit: string;
  /** Human placement name, e.g. "content-top" or "sidebar". */
  placement: string;
  /** Creative size, e.g. "728x90". */
  size?: string;
}

interface FillCounters {
  requests: number;
  impressions: number;
  unfilled: number;
  updatedAt: string;
}

function deviceClass(): "mobile" | "tablet" | "desktop" {
  if (typeof window === "undefined") return "desktop";
  const w = window.innerWidth;
  if (w < 640) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

/** Sport implied by the current path, so ad value per sport is comparable. */
function sportFromPath(path: string): string {
  const known = [
    "ufc", "mma", "boxing", "nfl", "nba", "mlb", "nhl", "ncaaf", "ncaab",
    "wnba", "tennis", "cricket", "rugby", "golf", "football", "soccer",
  ];
  const lower = path.toLowerCase();
  return known.find((s) => lower.includes(s)) ?? "general";
}

function baseProps(ctx: AdSlotContext) {
  const path = typeof window === "undefined" ? "" : window.location.pathname;
  return {
    ad_network: "adsense",
    ad_unit: ctx.unit,
    ad_placement: ctx.placement,
    ad_size: ctx.size ?? "responsive",
    page_path: path,
    sport: sportFromPath(path),
    device_class: deviceClass(),
  };
}

function readCounters(): FillCounters {
  const empty: FillCounters = { requests: 0, impressions: 0, unfilled: 0, updatedAt: new Date().toISOString() };
  if (typeof window === "undefined") return empty;
  try {
    const raw = localStorage.getItem(FILL_STORAGE_KEY);
    if (!raw) return empty;
    const parsed = JSON.parse(raw) as FillCounters;
    return typeof parsed?.requests === "number" ? parsed : empty;
  } catch {
    return empty;
  }
}

function writeCounters(next: FillCounters) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(FILL_STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* private mode — session-level numbers still reach GA4 */
  }
}

function bump(field: "requests" | "impressions" | "unfilled") {
  const counters = readCounters();
  counters[field] += 1;
  counters.updatedAt = new Date().toISOString();
  writeCounters(counters);
  return counters;
}

/** Fill rate observed on this browser so far (0–1), or null with no requests. */
export function getObservedFillRate(): number | null {
  const { requests, impressions } = readCounters();
  if (requests <= 0) return null;
  return impressions / requests;
}

export function trackAdRequest(ctx: AdSlotContext) {
  const counters = bump("requests");
  const props = baseProps(ctx);
  trackEvent("ad_request", { ...props, session_ad_requests: counters.requests });
  logAdEvent("request", props);
}

export function trackAdImpression(ctx: AdSlotContext) {
  const counters = bump("impressions");
  const props = baseProps(ctx);
  trackEvent("ad_impression", {
    ...props,
    session_ad_impressions: counters.impressions,
    session_fill_rate: Number((counters.impressions / Math.max(counters.requests, 1)).toFixed(3)),
  });
  logAdEvent("impression", props);
}

export function trackAdUnfilled(ctx: AdSlotContext) {
  const counters = bump("unfilled");
  const props = baseProps(ctx);
  trackEvent("ad_unfilled", { ...props, session_ad_unfilled: counters.unfilled });
  logAdEvent("unfilled", props);
}

export function trackAdClick(ctx: AdSlotContext) {
  rememberAdReferral(ctx);
  const props = baseProps(ctx);
  trackEvent("ad_click", props);
  logAdEvent("click", props);
}

/** Store the click so a later sign-up can be credited to the ad. */
function rememberAdReferral(ctx: AdSlotContext) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(
      ATTRIBUTION_KEY,
      JSON.stringify({
        network: "adsense",
        unit: ctx.unit,
        placement: ctx.placement,
        path: window.location.pathname,
        at: Date.now(),
      }),
    );
  } catch {
    /* ignore */
  }
}

export interface AdReferral {
  network: string;
  unit: string;
  placement: string;
  path: string;
  at: number;
}

/** The most recent ad click, if it is still inside the attribution window. */
export function getAdReferral(): AdReferral | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(ATTRIBUTION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AdReferral;
    if (!parsed?.at || Date.now() - parsed.at > ATTRIBUTION_WINDOW_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}
